import {
  WorkflowDefinitionDocument,
  WorkflowProcessDataField,
  WorkflowProcessInputBinding,
  WorkflowProcessOutputBinding,
} from './platform-workflow-definition-lifecycle.models';
import { StepAuthoringCommand, StepCommand } from './workflow-step-composition';
import { domainBlockCodes } from '@app/phms/workflow-blocks/domain-block-manifests';

/** Exact authored formats accepted by the installed process-data reader/compiler. */
export function supportsWorkflowProcessData(version: string | undefined | null): boolean {
  return ['1.10', '1.11', '1.12', '1.13', '1.14', '1.15', '1.16', '1.17', '1.18', '1.19', '1.20', '1.21', '1.22'].includes(version ?? '');
}

/** Unsupported wire content stays visible and cannot be normalized by this schema writer. */
export function processDataShapeError(value: unknown): string | null {
  if (value == null) return null;
  if (typeof value !== 'object' || Array.isArray(value)) return 'This saved shared-data format is unsupported. Its original configuration is retained.';
  const data = value as Record<string, unknown>;
  if (Object.keys(data).some(key => key !== 'fields') || !Array.isArray(data['fields']))
    return 'This saved shared-data configuration contains unsupported settings. Its original content is retained.';
  const keys = ['key', 'label', 'type', 'requiredAtStart', 'recordTypeCode'];
  if (data['fields'].some((value: unknown) => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return true;
    const field = value as Record<string, unknown>;
    return Object.keys(field).some(key => !keys.includes(key)) ||
      typeof field['key'] !== 'string' || typeof field['label'] !== 'string' ||
      typeof field['type'] !== 'string' || !['string', 'integer', 'number', 'boolean', 'date', 'record-reference'].includes(field['type']) ||
      (Object.hasOwn(field, 'requiredAtStart') && typeof field['requiredAtStart'] !== 'boolean') ||
      (field['recordTypeCode'] != null && (typeof field['recordTypeCode'] !== 'string' || field['type'] !== 'record-reference'));
  })) return 'This saved shared-data configuration contains unsupported value settings. Its original content is retained.';
  return null;
}

export interface ProcessActionField {
  name: string;
  label: string;
  type: string;
  recordTypeCode?: string | null;
}
const reserved = new Set([
  'occurrenceid',
  'expectedetag',
  'expectedtargetetag',
  'targetproceedingid',
  'valuesjson',
  'workflowid',
  'workflowinstanceid',
  'workflowstepinstanceid',
  'subjectid',
  'subjecttypecode',
  'userid',
  'actorid',
  'scopetypecode',
  'scopekey',
  'tenantkey',
  'requestid',
  'constructor',
  'prototype',
  '__proto__',
]);
export function processActionFields(
  command: StepCommand,
  installed: StepAuthoringCommand,
  direction: 'input' | 'output',
): ProcessActionField[] {
  if (!installed.supportsProcessDataBindings) return [];
  if (installed.supportsCaptureForm)
    return (command.captureForm?.fields ?? [])
      .filter((field) => !reserved.has(field.id.toLowerCase()))
      .map((field) => ({
        name: field.id,
        label: field.label,
        type: ['text', 'textarea', 'select', 'date'].includes(field.type)
          ? 'string'
          : field.type,
      }));
  return (
    (direction === 'input' ? installed.inputFields : installed.outputFields) ??
    []
  ).filter((field) => !reserved.has(field.name.toLowerCase()));
}
export function processBindingCompatible(
  field: ProcessActionField,
  data: WorkflowProcessDataField,
): boolean {
  if (
    field.recordTypeCode != null &&
    (data.type !== 'record-reference' ||
      data.recordTypeCode !== field.recordTypeCode)
  )
    return false;
  return data.type === 'record-reference'
    ? ['string', 'integer'].includes(field.type)
    : field.type === data.type ||
        (data.type === 'date' && field.type === 'string');
}
/** Exact local field issues also describe the selected native controls. */
export function processDataFieldIssues(field: WorkflowProcessDataField): Partial<Record<'key' | 'label' | 'type' | 'recordTypeCode', string>> {
  const issues: Partial<Record<'key' | 'label' | 'type' | 'recordTypeCode', string>> = {};
  if (!/^[a-z][a-z0-9]*(?:[-.][a-z0-9]+)*$/.test(field.key) || field.key.length > 120 || reserved.has(field.key.toLowerCase()))
    issues.key = 'Use a unique data key beginning with a letter, followed by lowercase letters, numbers, dots, or hyphens. System identity keys are reserved.';
  if (!field.label.trim() || field.label.length > 120) issues.label = 'Give each value a label of up to 120 characters.';
  if (!['string', 'integer', 'number', 'boolean', 'date', 'record-reference'].includes(field.type) ||
    (field.type !== 'record-reference' && field.recordTypeCode != null)) issues.type = `Choose a supported type for “${field.label || field.key}”.`;
  // This namespace is retained verbatim; the reader allows dots, underscores and hyphens.
  if (field.type === 'record-reference' && (!field.recordTypeCode || field.recordTypeCode.length > 120 || !/^[a-z][a-z0-9._-]*$/.test(field.recordTypeCode)))
    issues.recordTypeCode = `Choose a record type name for “${field.label || field.key}”.`;
  return issues;
}
export function processDataErrors(
  fields: WorkflowProcessDataField[],
  document: WorkflowDefinitionDocument,
): string[] {
  const errors: string[] = [],
    keys = new Set<string>();
  if (fields.length > 50) errors.push('Use no more than 50 shared values.');
  for (const field of fields) {
    errors.push(...Object.values(processDataFieldIssues(field)));
    if (keys.has(field.key)) errors.push(`The data key “${field.key}” is already used.`);
    keys.add(field.key);
  }
  return [...new Set([...errors, ...processDataDependencyErrors(fields, document)])];
}

/** Known retained consumers only; this is not a complete usage inventory. */
export function processDataDependencyErrors(
  fields: WorkflowProcessDataField[],
  document: WorkflowDefinitionDocument,
  affectedKeys?: ReadonlySet<string>,
): string[] {
  const errors: string[] = [], keys = new Set(fields.map(field => field.key));
  for (const node of document.nodes) {
    for (const command of node.declaration?.behavior.commands ?? [])
      for (const binding of [
        ...(command.inputBindings ?? []),
        ...(command.outputBindings ?? []),
      ].filter(binding => !affectedKeys || affectedKeys.has(binding.processDataKey)))
        if (!keys.has(binding.processDataKey))
          errors.push(
            `“${binding.processDataKey}” is used by an action. Remove its action bindings before deleting the value.`,
          );
        else {
          const before = document.processData?.fields.find(
            (field) => field.key === binding.processDataKey,
          );
          const after = fields.find(
            (field) => field.key === binding.processDataKey,
          )!;
          if (
            before &&
            (before.type !== after.type ||
              (before.recordTypeCode ?? null) !==
                (after.recordTypeCode ?? null))
          )
            errors.push(
              `“${after.label}” is used by an action. Remove its action bindings before changing its type.`,
            );
        }
    const bypass = node.stepProgressConfiguration?.bypass;
    if (bypass?.conditionCode === 'workflow.process-flag' && bypass.processDataKey && (!affectedKeys || affectedKeys.has(bypass.processDataKey)) &&
      fields.filter(field => field.key === bypass.processDataKey && field.type === 'boolean').length !== 1)
      errors.push(`“${bypass.processDataKey}” is used by a step bypass rule. Keep its yes/no key and type, or change the step's progress settings first.`);
    for (const block of node.declaration?.detail.blocks ?? []) {
      const settings = block.settings;
      if (!domainBlockCodes.has(block.code) || block.provider !== block.code.split('.')[0] ||
        settings?.['bindingMode'] !== 'process-reference' || typeof settings['referenceField'] !== 'string') continue;
      const key = settings['referenceField'];
      if (affectedKeys && !affectedKeys.has(key)) continue;
      const before = document.processData?.fields.find(field => field.key === key);
      const matches = fields.filter(field => field.key === key);
      if (matches.length !== 1 || matches[0].type !== 'record-reference' ||
        (before && (before.type !== matches[0].type || before.recordTypeCode !== matches[0].recordTypeCode)))
        errors.push(`“${key}” is used by a Staff page record source. Keep its reference key and type, or change that source in Staff page first.`);
    }
  }
  return [...new Set(errors)];
}
export function processBindingErrors(
  document: WorkflowDefinitionDocument,
  command: StepCommand,
  installed: StepAuthoringCommand,
  inputs: WorkflowProcessInputBinding[],
  outputs: WorkflowProcessOutputBinding[],
): string[] {
  const errors: string[] = [];
  for (const name of installed.requiredInputBindings ?? [])
    if (!inputs.some((binding) => binding.input === name)) {
      const field = processActionFields(command, installed, 'input').find((item) => item.name === name);
      errors.push(`Choose process data for “${field?.label || name}”; this action requires a mapped value.`);
    }
  if (
    !installed.supportsProcessDataBindings &&
    (inputs.length || outputs.length)
  )
    errors.push(
      'This installed action does not support process data bindings.',
    );
  for (const direction of ['input', 'output'] as const) {
    const seen = new Set<string>();
    const targets = new Set<string>();
    const bindings = direction === 'input' ? inputs : outputs;
    for (const binding of bindings) {
      const name = 'input' in binding ? binding.input : binding.output;
      const field = processActionFields(command, installed, direction).find(
        (item) => item.name === name,
      );
      const data = document.processData?.fields.find(
        (item) => item.key === binding.processDataKey,
      );
      if (seen.has(name)) errors.push(`Map “${name}” only once.`);
      if (direction === 'output' && targets.has(binding.processDataKey))
        errors.push(
          `Save only one result to “${binding.processDataKey}” in this action.`,
        );
      seen.add(name);
      targets.add(binding.processDataKey);
      if (!field || !data || !processBindingCompatible(field, data))
        errors.push(
          `Choose compatible process data for “${field?.label || name}”.`,
        );
      if (reserved.has(name.toLowerCase()))
        errors.push('System identity fields cannot be mapped.');
      if (
        direction === 'input' &&
        data?.type === 'record-reference' &&
        (!('projection' in binding) || binding.projection !== 'record-id')
      )
        errors.push(`Read the record ID for “${field?.label || name}”.`);
      if (
        direction === 'input' &&
        data?.type !== 'record-reference' &&
        'projection' in binding &&
        binding.projection != null
      )
        errors.push('Only record references use record ID projection.');
    }
  }
  return [...new Set(errors)];
}
